<template>
    <div class="hero-category-overlap">
        <section class="hero-wrapper">
            <div class="hero-background">
                <img :src="optimizedDesktopUrl"  alt="" class="hero-background__image hero-background__image--desktop" role="presentation" loading="lazy">
                <img :src="optimizedMobileUrl" alt="" class="hero-background__image hero-background__image--mobile" role="presentation" loading="lazy">
            </div>
            <div class="hero">
                <p v-if="banner.eyebrow && banner.eyebrow.text" :class="`eyebrow text-${banner.eyebrow.color}`" :aria-hidden="hideEyebrow">
                    {{ banner.eyebrow.text }}
                </p>
                <h1
                    v-if="banner.heading && banner.heading.text"
                    :class="`headline-xl text-${banner.heading.color}`"
                    role="heading"
                    aria-level="2"
                    v-html="banner.heading.text"
                ></h1>
                <p
                    v-if="banner.description && banner.description.text"
                    v-html="banner.description.text"
                    :class="`paragraph-feature text-${banner.description.color}`"
                ></p>
            </div>
        </section>

        <section class="carousel-container contained-content">
            <div class="carousel-wrapper">
                <vue-horizontal
                    responsive scroll
                    class="carousel-items carousel-controls"
                >
                    <template v-slot:btn-prev>
                        <button
                            type="button"
                            class="left-arrow"
                            aria-label="Scroll left"
                            role="button"
                        >
                            <WhiteCarret alt="" />
                        </button>
                    </template>

                    <template v-slot:btn-next>
                        <button
                            type="button"
                            class="right-arrow"
                            aria-label="Scroll right"
                            role="button"
                        >
                            <WhiteCarret alt="" />
                        </button>
                    </template>

                    <span
                        v-for="(slide, index) in cards"
                        :key="index"
                        class="carousel-item"
                    >
                        <a :class="`carousel-item-content card-banner-card-hover${disableCardHover ? '-disabled' : ''}`" 
                            aria-label="Shop all shirts"
                            role="button"
                            tabindex="0"
                        >
                            <img
                                :src="slide.image"
                                alt="Person wearing shirt"
                            />
                            <span class="subtitle headline-xs" role="heading" aria-level="3">{{ slide.title }}</span>
                        </a>
                    </span>
                </vue-horizontal>
            </div>
        </section>
    </div>
</template>

<script lang="ts" setup>
    import type { PropType } from "vue";
    import { onMounted, onBeforeUnmount, nextTick, computed } from "vue";
    import HeroBanner from "@/components/HeroBanner.vue";
    import { type HeroBannerInterface } from "@interfaces/HeroBanner";
    import { usePage } from "@inertiajs/vue3";
    import VueHorizontal from "vue-horizontal";
    import useImageOptimization from "@composables/useImageOptimization";
    import WhiteCarret from "@icons/carrets-white.svg";
    let { banner, cards, disableCardHover, backgroundColor, margin, cardType } = defineProps({
        banner: {
            type: Object as PropType<Object>,
            required: true
        },
        cards: {
            type: Array as PropType<Object>,
            required: true
        },
        disableCardHover: {
            type: Boolean,
            default: true,
        },
        backgroundColor: {
            type: String,
            default: "#FFF"
        },
        margin: {
            type: Number,
            default: 0
        },
        cardType: {
            type: String,
            default: "Card"
        }
    });

    const bgImages = computed(()=>{
        return {
            "--desktop-img-url": `url(${optimizedDesktopUrl.value})`,
            "--mobile-img-url": `url(${optimizedMobileUrl.value})`,
        }
    });

    const breadcrumbs = computed(() => usePage().props.breadcrumbs || []);

    let bannerEl;
    const handleResize = () => {
        nextTick(() => {
            if (bannerEl?.offsetHeight) {
                bannerEl.style.marginBottom = `${bannerEl.offsetHeight * 0.4}px`;
            }
        });
    };
   

    let { optimizedImageUrl: optimizedDesktopUrl } = useImageOptimization(banner.banner_media.desktop_media);
    let { optimizedImageUrl: optimizedMobileUrl } = useImageOptimization(banner.banner_media.mobile_media);
    onMounted(() => {
        const observer = new ResizeObserver(handleResize);
        bannerEl = document.getElementById("banner");
        if(bannerEl){
            observer.observe(bannerEl);
            handleResize();
        }
        
    });

    onBeforeUnmount(() => {
        observer.disconnect();
    });

    let hideEyebrow = computed(() => {
        return !banner.eyebrow || !banner.eyebrow.text || banner.eyebrow.text.trim() === "";
    });
</script>

<style lang="scss" scoped>
    @import "@assets/styles/variables.scss";
    @import "@assets/styles/rothcoStyles.scss";
    @import "@assets/styles/rothco/plp.scss";
    .use-background-image {
        background-image: var(--mobile-img-url);
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        @media (min-width: $breakpoint-desktop) {
            background-image: var(--desktop-img-url);
            background-size: cover;
            background-repeat: no-repeat;
            background-position: center;
        }
    }
    .card-banner-card-hover {
        padding: 0 0.9375rem; 
        max-width: 68.375rem;
    }
    .card-banner-card-hover-disabled {
        pointer-events: none; 
        padding: 0 0.9375rem; 
        max-width: 68.375rem;
    }
</style>
